import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Download, Eye, ExternalLink, Minus, Paperclip, RotateCcw, Send, Square } from 'lucide-react';
import { useI18n } from '../i18n';
import { AgentMarkdown } from './AgentMarkdown';

const DEFAULT_VISIBLE_MESSAGE_COUNT = 6;

const GENERATION_TERMINAL = new Set(['ready', 'failed', 'cancelled']);

// Derive a short object name from a free-text generation prompt.
// Strips leading action/article words, keeps the first 3–4 content words, title-cases.
function promptToLabel(prompt: string): string {
  const STRIP = /^(please\s+)?(generate|create|add|place|make|build|design|show|put|a|an|the|me\s+a|me\s+an|me\s+the)\s+/i;
  let s = prompt.trim();
  // Strip filler prefix up to 3 times (e.g. "generate a modern sofa" → "modern sofa")
  for (let i = 0; i < 3; i++) { const t = s.replace(STRIP, ''); if (t === s) break; s = t; }
  const words = s.split(/\s+/).slice(0, 4).map(w => w.replace(/[,;:]+$/, ''));
  const label = words.join(' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

interface GenerationSession {
  sceneId: string;
  prompt: string;
  fluxJob: FluxJob | null;
  trellisJob: TrellisJob | null;
  selected: FluxCandidate | null;
  error: string | null;
  placeWithErase?: boolean;
  suggestedDimensions?: { width?: number; depth?: number; length?: number };
}

interface ObjectUploadSession {
  sceneId: string;
  file: File | null;
  label: string;
  job: TrellisJob | null;
  error: string | null;
  configured: boolean | null;
}

interface ModelUploadSession {
  file: File | null;
  label: string;
  error: string | null;
}

interface ReportTemplateSession {
  file: File | null;
  title: string;
  generating: boolean;
  error: string | null;
}

interface EraseOfferSession {
  userQuery: string;
}

function ThinkingIndicator({
  currentStatus,
  lastStreamEventAt,
  phrases,
  strings,
}: {
  currentStatus?: string;
  lastStreamEventAt?: number | null;
  phrases: string[];
  strings: { waitingFromStatus: (s: string) => string; waitingBroadAnalysis: string; stillWorking: string; takingLonger: string };
}) {
  const [idx, setIdx]               = useState(0);
  const [visible, setVisible]       = useState(true);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [idleSec, setIdleSec]       = useState(0);
  const startedAtRef                = useRef(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setElapsedSec(Math.floor((now - startedAtRef.current) / 1000));
      setIdleSec(lastStreamEventAt ? Math.floor((now - lastStreamEventAt) / 1000) : 0);
      setVisible(false);
      setTimeout(() => {
        setIdx((prev) => (prev + 1) % phrases.length);
        setVisible(true);
      }, 250);
    }, 2500);

    return () => clearInterval(timer);
  }, [lastStreamEventAt, phrases.length]);

  const longRunningMessage =
    idleSec >= 90 && currentStatus
      ? strings.waitingFromStatus(currentStatus)
      : elapsedSec >= 90
      ? strings.waitingBroadAnalysis
      : elapsedSec >= 45
        ? strings.stillWorking
        : elapsedSec >= 20
          ? strings.takingLonger
          : null;
  const primaryText = currentStatus || phrases[idx];

  return (
    <div className="space-y-1">
      <p
        className="text-sm text-white/50 transition-opacity duration-250"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {primaryText} <span className="text-white/30">{elapsedSec}s</span>
      </p>
      {lastStreamEventAt && idleSec >= 20 && (
        <p className="text-xs text-white/35">Last SI update {idleSec}s ago.</p>
      )}
      {longRunningMessage && (
        <p className="text-xs text-white/35">{longRunningMessage}</p>
      )}
    </div>
  );
}

import type { POIData } from './POIOverlay';
import type { SceneCapabilityStates, SceneConfig } from '../types/sceneConfig';
import type { ViewerSpatialContext } from '../utils/viewerSpatialUtils';
import {
  buildViewerAgentCapabilities,
  generateViewerReportPdf,
  sendViewerAgentMessageStream,
  ViewerAgentStreamEvent,
} from '../services/viewerAgent.service';
import type { ViewerAgentChatResponse } from '../types/viewerAgent';
import { AgentArtifactViewer, getAgentArtifact, type AgentArtifact } from './AgentArtifactViewer';
import type { EncroachmentRow } from '../types/viewerAgent';
import { cancelFluxJob, createFluxJob, getFluxJob, type FluxCandidate, type FluxJob } from '../services/flux.service';
import { cancelTrellisJob, createTrellisJob, createTrellisJobFromCandidate, getTrellisConfig, getTrellisJob, type TrellisGeneratedAsset, type TrellisJob } from '../services/trellis.service';

interface ViewerOrchestrationAgentProps {
  onPinsReady?: (url: string) => void;
  scene: SceneConfig;
  capabilities: SceneCapabilityStates;
  pois: POIData[];
  disabled?: boolean;
  ready?: boolean;
  startMinimized?: boolean;
  onDecision: (response: ViewerAgentChatResponse) => void;
  onGeneratedAssetReady?: (asset: TrellisGeneratedAsset) => void;
  /** Called instead of onGeneratedAssetReady when the Trellis job was started with placeWithErase=true. */
  onTrellisPlacementReady?: (asset: TrellisGeneratedAsset) => void;
  captureScreenshot?: () => Promise<string | null>;
  getCameraPosition?: () => { latitude: number; longitude: number; altitude: number } | null;
  getViewerSpatialContext?: () => ViewerSpatialContext | null;
  externalSubmitRef?: React.MutableRefObject<((text: string) => void) | null>;
  onUserMessage?: (text: string) => void;
  onPillClick?: (tag: string, text: string) => void;
}

interface DimensionPolygon {
  id: string;
  label: string;
  worldCorners: [number, number, number][];
}

interface AgentMessage {
  role: 'user' | 'agent';
  text: string;
  siEntries?: LiveStreamEntry[];
  pendingDelegation?: { spatialQuestion: string; reconstructionId: string };
  encroachmentRows?: EncroachmentRow[];
  assetUrl?: string;
  mtlUrl?: string;
  textureUrls?: Record<string, string>;
  siFiles?: string[];
  siFileSizes?: number[];
  gausViewerHints?: { lookAt: [number, number, number]; camPos: [number, number, number]; fov?: number; halfSize?: number };
  dimensionPolygons?: DimensionPolygon[];
  warnings?: string[];
}

interface LiveStreamEntry {
  id: number;
  kind: 'analysis' | 'progress' | 'file' | 'warning';
  text: string;
}

function basename(path: string): string {
  return path.split('/').filter(Boolean).pop() || path;
}

function truncateMiddle(value: string, max = 180): string {
  if (value.length <= max) return value;
  const head = Math.floor((max - 3) * 0.6);
  const tail = max - 3 - head;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

function toolProgressLabel(name: string, input: Record<string, unknown> = {}): string {
  const toolName = name || 'tool';
  const path = String(input.file_path ?? input.path ?? '');
  if (path) return `${toolName}: ${basename(path)}`;

  if (name === 'Bash') {
    const command = String(input.command ?? input.cmd ?? '');
    return command ? `Bash: ${truncateMiddle(command)}` : 'Bash';
  }

  const keys = Object.keys(input);
  if (keys.length > 0) {
    const summary = truncateMiddle(JSON.stringify(input));
    return `${toolName}: ${summary}`;
  }

  return toolName;
}

function splitStreamText(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

// ─── SI content renderer ──────────────────────────────────────────────────────
// Merges consecutive analysis chunks into flowing prose; tool steps become
// muted one-line annotations; files and warnings get inline treatment.

type SIRenderGroup =
  | { kind: 'text';    text: string }
  | { kind: 'tool';    text: string }
  | { kind: 'file';    text: string }
  | { kind: 'warning'; text: string };

function groupSIEntries(entries: LiveStreamEntry[]): SIRenderGroup[] {
  const groups: SIRenderGroup[] = [];
  for (const entry of entries) {
    if (entry.kind === 'analysis') {
      const last = groups[groups.length - 1];
      if (last?.kind === 'text') {
        last.text += '\n\n' + entry.text;
      } else {
        groups.push({ kind: 'text', text: entry.text });
      }
    } else if (entry.kind === 'progress') {
      groups.push({ kind: 'tool', text: entry.text });
    } else if (entry.kind === 'file') {
      groups.push({ kind: 'file', text: entry.text });
    } else if (entry.kind === 'warning') {
      groups.push({ kind: 'warning', text: entry.text });
    }
  }
  return groups;
}

function SIContent({
  entries,
  onOpenArtifact,
}: {
  entries: LiveStreamEntry[];
  onOpenArtifact?: (artifact: AgentArtifact) => void;
}) {
  const { t } = useI18n();
  const va = t.viewerAgent;
  const groups = groupSIEntries(entries);
  if (!groups.length) return null;

  return (
    <div className="space-y-2.5">
      {groups.map((group, i) => {
        if (group.kind === 'text') {
          return (
            <div key={i} className="text-sm leading-relaxed text-white/90">
              <AgentMarkdown text={group.text} />
            </div>
          );
        }
        if (group.kind === 'tool') {
          return (
            <div key={i} className="flex items-center gap-1.5 text-xs text-white/30">
              <span className="shrink-0">↳</span>
              <span>{group.text}</span>
            </div>
          );
        }
        if (group.kind === 'file') {
          const artifact = getAgentArtifact(group.text);
          const isSupported = artifact.kind !== 'unknown' && (artifact.kind !== 'pins' || !!artifact.url);
          return (
            <div key={i} className="flex items-start gap-2 rounded border border-emerald-300/25 bg-emerald-400/10 px-2 py-1.5">
              <span className="mt-0.5 shrink-0 text-[10px] text-emerald-300/70">↳</span>
              <div className="min-w-0 flex-1">
                <code className="block text-xs font-semibold text-emerald-100">{artifact.label}</code>
                {artifact.kind !== 'obj' && (
                  <code className="mt-0.5 block break-all text-[10px] text-emerald-100/50">{group.text}</code>
                )}
                {isSupported && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => onOpenArtifact?.(artifact)}
                      className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      {artifact.kind === 'pins' ? t.pins.load : va.preview}
                    </button>
                    {artifact.url && (
                      <a
                        href={artifact.url}
                        target="_blank"
                        rel="noreferrer"
                        {...(artifact.kind === 'obj' ? { download: artifact.label } : {})}
                        className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                      >
                        {artifact.kind === 'obj'
                          ? <><Download className="h-3.5 w-3.5" />{va.download}</>
                          : <><ExternalLink className="h-3.5 w-3.5" />Open tab</>
                        }
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        }
        if (group.kind === 'warning') {
          return (
            <p key={i} className="text-xs italic text-amber-300/70">{group.text}</p>
          );
        }
        return null;
      })}
    </div>
  );
}

// ─── SI trail (collapsed summary in agent message after response) ─────────────

function SITrail({
  entries,
  onOpenArtifact,
}: {
  entries: LiveStreamEntry[];
  onOpenArtifact?: (artifact: AgentArtifact) => void;
}) {
  const { t } = useI18n();
  const va = t.viewerAgent;
  const [open, setOpen] = useState(false);
  const fileCount = entries.filter((e) => e.kind === 'file').length;
  const label = fileCount > 0 ? va.spatialAnalysisFiles(fileCount) : va.spatialAnalysis;

  return (
    <div className="mt-2 border-t border-white/8 pt-2">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="text-[11px] text-[#0CB4E4]/60 transition-colors hover:text-[#0CB4E4]/90"
      >
        {open ? '▲' : '▼'} {label}
      </button>
      {open && (
        <div className="mt-3">
          <SIContent entries={entries} onOpenArtifact={onOpenArtifact} />
        </div>
      )}
    </div>
  );
}

// ─── Encroachment risk table ──────────────────────────────────────────────────

const RISK_STYLES: Record<EncroachmentRow['riskLevel'], { badge: string; row: string }> = {
  high:   { badge: 'bg-red-500/20 text-red-300 border-red-500/30',    row: 'hover:bg-red-500/8' },
  medium: { badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30', row: 'hover:bg-amber-500/8' },
  low:    { badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', row: 'hover:bg-emerald-500/8' },
};

function EncroachmentTable({
  rows,
  onTeleport,
}: {
  rows: EncroachmentRow[];
  onTeleport: (row: EncroachmentRow) => void;
}) {
  const sorted = [...rows].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.riskLevel] - order[b.riskLevel];
  });

  return (
    <div className="mt-2 overflow-hidden rounded-md border border-white/10">
      <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.04] px-3 py-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-white/50">
          Encroachment Risk · {rows.length} spot{rows.length !== 1 ? 's' : ''}
        </span>
        <span className="text-[10px] text-white/30">Click row to fly there</span>
      </div>
      <div className="max-h-[260px] overflow-y-auto">
        {sorted.map((row) => {
          const style = RISK_STYLES[row.riskLevel];
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => onTeleport(row)}
              className={`flex w-full items-start gap-2.5 border-b border-white/6 px-3 py-2 text-left transition-colors last:border-0 ${style.row}`}
            >
              <span className={`mt-0.5 shrink-0 rounded border px-1.5 py-px text-[10px] font-semibold uppercase ${style.badge}`}>
                {row.riskLevel}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-white/90">{row.label}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-white/45">
                  <span>{row.vegetationType}</span>
                  <span>·</span>
                  <span>{row.distanceToLine} from line</span>
                  {row.notes && <><span>·</span><span className="truncate">{row.notes}</span></>}
                </div>
              </div>
              <span className="mt-1 shrink-0 text-[10px] text-white/25">↗</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function detectUploadType(file: File): 'image' | 'model' | 'document' | null {
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp|tiff?)$/.test(name)) return 'image';
  if (/\.(glb|gltf)$/.test(name) || mime.includes('gltf')) return 'model';
  if (mime === 'application/pdf' || /\.pdf$/.test(name) || mime.includes('wordprocessingml') || /\.docx?$/.test(name)) return 'document';
  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────

const ViewerOrchestrationAgent: React.FC<ViewerOrchestrationAgentProps> = ({
  scene,
  capabilities,
  pois,
  disabled = false,
  ready = true,
  startMinimized = false,
  onDecision,
  onGeneratedAssetReady,
  onTrellisPlacementReady,
  captureScreenshot,
  getCameraPosition,
  getViewerSpatialContext,
  externalSubmitRef,
  onUserMessage,
  onPillClick,
  onPinsReady,
}) => {
  const { lang, t, toggle } = useI18n();
  const va = t.viewerAgent;

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(startMinimized);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [liveAgentText, setLiveAgentText] = useState('');
  const [liveStatus, setLiveStatus] = useState('');
  const [lastStreamEventAt, setLastStreamEventAt] = useState<number | null>(null);
  const [liveEntries, setLiveEntries] = useState<LiveStreamEntry[]>([]);
  const [selectedArtifact, setSelectedArtifact] = useState<AgentArtifact | null>(null);
  const openArtifact = useCallback((artifact: AgentArtifact) => {
    if (artifact.kind === 'pins' && artifact.url && onPinsReady) {
      onPinsReady(artifact.url);
      return;
    }
    setSelectedArtifact(artifact);
  }, [onPinsReady]);
  const [generationSession, setGenerationSession] = useState<GenerationSession | null>(null);
  const [objectUploadSession, setObjectUploadSession] = useState<ObjectUploadSession | null>(null);
  const [modelUploadSession, setModelUploadSession] = useState<ModelUploadSession | null>(null);
  const [reportTemplateSession, setReportTemplateSession] = useState<ReportTemplateSession | null>(null);
  const [eraseOfferSession, setEraseOfferSession] = useState<EraseOfferSession | null>(null);
  const [pendingUpload, setPendingUpload] = useState<{ file: File; type: 'image' | 'model' | 'document' } | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  // Persists across the whole conversation so the user never needs to re-upload
  const pendingDocumentFileRef   = useRef<File | null>(null);
  const pendingDocumentBase64Ref = useRef<string | null>(null);
  const pendingImageBase64Ref    = useRef<string | null>(null);
  const autoSubmittedRef        = useRef(false);
  const userInteractedRef       = useRef(false);
  const handleSubmitRef         = useRef<((text: string) => void) | null>(null);
  const welcomeQuestionRef      = useRef(t.viewerAgent.welcomeQuestion);
  const captureScreenshotRef    = useRef(captureScreenshot);
  const deliveredAssetRef       = useRef<string | null>(null);
  const onGeneratedAssetReadyRef = useRef(onGeneratedAssetReady);
  const messagesEndRef       = useRef<HTMLDivElement>(null);
  const chatRootRef          = useRef<HTMLDivElement>(null);
  const liveAgentTextRef     = useRef('');
  const liveEntriesRef       = useRef<LiveStreamEntry[]>([]);
  const liveEntryIdRef       = useRef(0);
  // Stable conversation ID — persists across turns so SI sessions can resume
  const conversationIdRef    = useRef(`conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const abortControllerRef      = useRef<AbortController | null>(null);
  const [pendingTaskPrompt, setPendingTaskPrompt] = useState<string | null>(null);
  const pendingTaskTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onTrellisPlacementReadyRef = useRef(onTrellisPlacementReady);
  useEffect(() => { captureScreenshotRef.current = captureScreenshot; }, [captureScreenshot]);
  useEffect(() => { onGeneratedAssetReadyRef.current = onGeneratedAssetReady; }, [onGeneratedAssetReady]);
  useEffect(() => { onTrellisPlacementReadyRef.current = onTrellisPlacementReady; }, [onTrellisPlacementReady]);

  useEffect(() => () => { if (pendingTaskTimerRef.current) clearTimeout(pendingTaskTimerRef.current); }, []);

  useEffect(() => {
    if (pendingTaskTimerRef.current) { clearTimeout(pendingTaskTimerRef.current); pendingTaskTimerRef.current = null; }
    setPendingTaskPrompt(null);
    setGenerationSession(null);
    setObjectUploadSession(null);
    setModelUploadSession(null);
    setReportTemplateSession(null);
    setEraseOfferSession(null);
    setPendingUpload(null);
    pendingDocumentFileRef.current = null;
    pendingDocumentBase64Ref.current = null;
    deliveredAssetRef.current = null;
  }, [scene.sceneId]);

  // Flux polling — runs while the flux job is in a non-terminal state
  const genFluxJobId = generationSession?.fluxJob?.jobId;
  const genFluxStatus = generationSession?.fluxJob?.status;
  useEffect(() => {
    if (!genFluxJobId || !genFluxStatus || GENERATION_TERMINAL.has(genFluxStatus)) return;
    const timer = window.setInterval(() => {
      void getFluxJob(genFluxJobId)
        .then(job => setGenerationSession(prev => prev ? { ...prev, fluxJob: job } : prev))
        .catch(err => setGenerationSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Image status failed' } : prev));
    }, 1200);
    return () => window.clearInterval(timer);
  }, [genFluxJobId, genFluxStatus]);

  // Rescue fetch: if createFluxJob returns a terminal job with no candidates (fast/cached
  // backend response), do one GET to retrieve the populated candidates.
  const genFluxTerminalNoCandidates = Boolean(
    genFluxJobId &&
    genFluxStatus &&
    GENERATION_TERMINAL.has(genFluxStatus) &&
    (generationSession?.fluxJob?.candidates ?? []).length === 0,
  );
  useEffect(() => {
    if (!genFluxTerminalNoCandidates || !genFluxJobId) return;
    void getFluxJob(genFluxJobId)
      .then(job => setGenerationSession(prev => prev ? { ...prev, fluxJob: job } : prev))
      .catch(() => { /* no-op — user can Try again */ });
  }, [genFluxJobId, genFluxTerminalNoCandidates]);

  // Trellis polling — runs while the trellis job is in a non-terminal state
  const genTrellisJobId = generationSession?.trellisJob?.jobId;
  const genTrellisStatus = generationSession?.trellisJob?.status;
  const genPlaceWithErase = generationSession?.placeWithErase;
  const genSuggestedDimensions = generationSession?.suggestedDimensions;
  useEffect(() => {
    if (!genTrellisJobId || !genTrellisStatus || GENERATION_TERMINAL.has(genTrellisStatus)) return;
    const timer = window.setInterval(() => {
      void getTrellisJob(genTrellisJobId)
        .then(job => {
          setGenerationSession(prev => prev ? { ...prev, trellisJob: job } : prev);
          if (job.status === 'ready' && job.asset && deliveredAssetRef.current !== job.jobId) {
            deliveredAssetRef.current = job.jobId;
            const asset = genSuggestedDimensions ? { ...job.asset, suggestedDimensions: genSuggestedDimensions } : job.asset;
            if (genPlaceWithErase) {
              onTrellisPlacementReadyRef.current?.(asset);
              setMessages(prev => [...prev, { role: 'agent', text: '3D model ready — click the road to position it.' }]);
            } else {
              onGeneratedAssetReadyRef.current?.(asset);
              setMessages(prev => [...prev, { role: 'agent', text: 'Your 3D model is ready — click anywhere in the scene to place it, then use the toolbox to resize and rotate.' }]);
            }
            setGenerationSession(null);
          }
        })
        .catch(err => setGenerationSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : '3D generation failed' } : prev));
    }, 1500);
    return () => window.clearInterval(timer);
  }, [genTrellisJobId, genTrellisStatus, genPlaceWithErase, genSuggestedDimensions]);

  // Fetch Trellis config when object-upload panel opens
  const objSceneId = objectUploadSession?.sceneId;
  useEffect(() => {
    if (!objSceneId) return;
    void getTrellisConfig()
      .then(config => setObjectUploadSession(prev => prev ? { ...prev, configured: config.configured } : prev))
      .catch(err => setObjectUploadSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Could not check generation service' } : prev));
  }, [objSceneId]);

  // Poll Trellis job created from user-uploaded image
  const objJobId = objectUploadSession?.job?.jobId;
  const objJobStatus = objectUploadSession?.job?.status;
  useEffect(() => {
    if (!objJobId || !objJobStatus || GENERATION_TERMINAL.has(objJobStatus)) return;
    const timer = window.setInterval(() => {
      void getTrellisJob(objJobId)
        .then(job => {
          setObjectUploadSession(prev => prev ? { ...prev, job } : prev);
          if (job.status === 'ready' && job.asset && deliveredAssetRef.current !== job.jobId) {
            deliveredAssetRef.current = job.jobId;
            onGeneratedAssetReadyRef.current?.(job.asset);
            setMessages(prev => [...prev, { role: 'agent', text: 'Your 3D model is ready — click anywhere in the scene to place it, then use the toolbox to resize and rotate.' }]);
            setObjectUploadSession(null);
          }
        })
        .catch(err => setObjectUploadSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Generation status failed' } : prev));
    }, 1500);
    return () => window.clearInterval(timer);
  }, [objJobId, objJobStatus]);

  // Block viewer keyboard shortcuts (e.g. 'c' for point cloud) while chatting.
  // React synthetic stopPropagation does not reliably block native DOM listeners.
  useEffect(() => {
    const el = chatRootRef.current;
    if (!el) return;
    const stop = (e: KeyboardEvent) => e.stopPropagation();
    el.addEventListener('keydown', stop);
    el.addEventListener('keyup', stop);
    el.addEventListener('keypress', stop);
    return () => {
      el.removeEventListener('keydown', stop);
      el.removeEventListener('keyup', stop);
      el.removeEventListener('keypress', stop);
    };
  }, []);

  const stopViewerShortcuts = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation?.();
  };

  const appendLiveEntry = useCallback((entry: Omit<LiveStreamEntry, 'id'>) => {
    setLiveEntries((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.kind === entry.kind && last.text === entry.text) return prev;
      liveEntryIdRef.current += 1;
      const next = [...prev, { ...entry, id: liveEntryIdRef.current }].slice(-24);
      liveEntriesRef.current = next;
      return next;
    });
  }, []);

  const cancelGeneration = useCallback(() => {
    if (generationSession?.fluxJob && !GENERATION_TERMINAL.has(generationSession.fluxJob.status)) {
      void cancelFluxJob(generationSession.fluxJob.jobId);
    }
    if (generationSession?.trellisJob && !GENERATION_TERMINAL.has(generationSession.trellisJob.status)) {
      void cancelTrellisJob(generationSession.trellisJob.jobId);
    }
    setGenerationSession(null);
  }, [generationSession]);

  const regenerateFlux = useCallback(() => {
    if (!generationSession) return;
    const { sceneId, prompt } = generationSession;
    setGenerationSession(prev => prev ? { ...prev, fluxJob: null, selected: null, trellisJob: null, error: null } : prev);
    void createFluxJob(sceneId, prompt, 3)
      .then(job => setGenerationSession(prev => prev ? { ...prev, fluxJob: job } : prev))
      .catch(err => setGenerationSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Image generation failed' } : prev));
  }, [generationSession]);

  const startTrellisFromSelection = useCallback(async () => {
    if (!generationSession?.selected || !generationSession.fluxJob) return;
    setGenerationSession(prev => prev ? { ...prev, error: null } : prev);
    try {
      const job = await createTrellisJobFromCandidate(
        generationSession.sceneId,
        promptToLabel(generationSession.prompt),
        generationSession.fluxJob.jobId,
        generationSession.selected.candidateId,
      );
      setGenerationSession(prev => prev ? { ...prev, trellisJob: job } : prev);
    } catch (err) {
      setGenerationSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Could not start 3D generation' } : prev);
    }
  }, [generationSession]);

  const cancelObjectUpload = useCallback(() => {
    if (objectUploadSession?.job && !GENERATION_TERMINAL.has(objectUploadSession.job.status)) {
      void cancelTrellisJob(objectUploadSession.job.jobId);
    }
    setObjectUploadSession(null);
  }, [objectUploadSession]);

  const handleFileSelected = useCallback((file: File) => {
    const type = detectUploadType(file);
    if (!type) return;
    const reader = new FileReader();
    if (type === 'document') {
      pendingDocumentFileRef.current = file;
      reader.onload = () => {
        pendingDocumentBase64Ref.current = (reader.result as string).replace(/^data:[^;]+;base64,/, '');
      };
      reader.readAsDataURL(file);
    } else if (type === 'image') {
      reader.onload = () => {
        pendingImageBase64Ref.current = (reader.result as string).replace(/^data:[^;]+;base64,/, '');
      };
      reader.readAsDataURL(file);
    }
    setPendingUpload({ file, type });
    setExpanded(true);
  }, []);

  const acceptImageForGeneration = useCallback(() => {
    if (!pendingUpload) return;
    const { file } = pendingUpload;
    setPendingUpload(null);
    const label = file.name.replace(/\.[^.]+$/, '');
    setObjectUploadSession({ sceneId: scene.sceneId, file, label, job: null, error: null, configured: null });
  }, [pendingUpload, scene.sceneId]);

  const placeUploadedModel = useCallback(() => {
    if (!pendingUpload?.file) return;
    const { file } = pendingUpload;
    const label = file.name.replace(/\.[^.]+$/, '');
    setPendingUpload(null);
    const blobUrl = URL.createObjectURL(file);
    onGeneratedAssetReadyRef.current?.({ assetId: `upload-${Date.now()}`, label, glbUrl: blobUrl, source: 'trellis' });
    setMessages(prev => [...prev, {
      role: 'agent',
      text: `"${label}" has been placed in the scene — use the toolbox to position and scale it.`,
    }]);
  }, [pendingUpload]);


  const startObjectGeneration = useCallback(async () => {
    if (!objectUploadSession?.file) return;
    setObjectUploadSession(prev => prev ? { ...prev, error: null } : prev);
    try {
      const job = await createTrellisJob(objectUploadSession.file, objectUploadSession.sceneId, objectUploadSession.label);
      setObjectUploadSession(prev => prev ? { ...prev, job } : prev);
    } catch (err) {
      setObjectUploadSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Could not start generation' } : prev);
    }
  }, [objectUploadSession]);

  const placeModelFromUpload = useCallback(() => {
    const session = modelUploadSession;
    if (!session?.file) return;
    const blobUrl = URL.createObjectURL(session.file);
    onGeneratedAssetReadyRef.current?.({
      assetId: `upload-${Date.now()}`,
      label: session.label || session.file.name.replace(/\.[^.]+$/, ''),
      glbUrl: blobUrl,
      source: 'trellis',
    });
    setMessages(prev => [...prev, {
      role: 'agent',
      text: `"${session.label || session.file!.name}" has been placed in the scene — use the toolbox to position and scale it.`,
    }]);
    setModelUploadSession(null);
  }, [modelUploadSession]);

  const generateReportFromTemplate = useCallback(async () => {
    if (!reportTemplateSession?.file) return;
    const reportSource = [...messages].reverse().find(message => {
      if (message.role !== 'agent') return false;
      const streamedAnalysis = (message.siEntries ?? [])
        .filter(entry => entry.kind === 'analysis')
        .map(entry => entry.text)
        .join('\n');
      const candidate = `${message.text}\n${streamedAnalysis}`;
      return candidate.length > 500 && /(?:completion|progress|outstanding|summary|\d+%)/i.test(candidate);
    });
    const streamedAnalysis = (reportSource?.siEntries ?? [])
      .filter(entry => entry.kind === 'analysis')
      .map(entry => entry.text)
      .join('\n\n');
    const sourceText = reportSource?.text && reportSource.text.length >= streamedAnalysis.length
      ? reportSource.text
      : streamedAnalysis;
    if (!sourceText.trim()) {
      setReportTemplateSession(prev => prev ? { ...prev, error: 'No substantive construction progress analysis is available yet.' } : prev);
      return;
    }
    setReportTemplateSession(prev => prev ? { ...prev, generating: true, error: null } : prev);
    try {
      const blob = await generateViewerReportPdf({
        template: reportTemplateSession.file,
        sceneName: scene.sceneId,
        title: reportTemplateSession.title || 'Construction Progress Report',
        sourceText,
      });
      const url = URL.createObjectURL(blob);
      const filename = `${scene.sceneId.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()}-progress-report.pdf`;
      setSelectedArtifact({ label: filename, path: filename, url, kind: 'pdf', canPreview: true });
      setMessages(prev => [...prev, { role: 'agent', text: `Your PDF report is ready: ${filename}`, assetUrl: url }]);
      setReportTemplateSession(null);
    } catch (error) {
      setReportTemplateSession(prev => prev ? { ...prev, generating: false, error: error instanceof Error ? error.message : 'Could not generate the report.' } : prev);
    }
  }, [messages, reportTemplateSession, scene.sceneId]);

  const placeholder = useMemo(() => {
    if (disabled) return va.phLoading;
    if (isThinking) return va.phPlanning;
    return va.phAsk;
  }, [disabled, isThinking, va]);

  const handleSubmit = useCallback(async (text: string) => {
    if (!text || isThinking) return;

    // If an image is pending and user wants to generate a 3D model, route straight to Trellis.
    const lc = text.toLowerCase();
    if (pendingUpload?.type === 'image' && (lc.includes('generate') || lc.includes('3d') || lc.includes('model'))) {
      const file = pendingUpload.file;
      const label = file.name.replace(/\.[^.]+$/, '');
      setPendingUpload(null);
      pendingImageBase64Ref.current = null;
      setInput('');
      setObjectUploadSession({ sceneId: scene.sceneId, file, label, job: null, error: null, configured: null });
      setMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: `Starting 3D generation for "${label}"…` }]);
      return;
    }

    onUserMessage?.(text);
    if (pendingTaskTimerRef.current) { clearTimeout(pendingTaskTimerRef.current); pendingTaskTimerRef.current = null; }
    setPendingTaskPrompt(null);

    // Consume the pending image — use ref (always current) not state (stale closure).
    const pendingImageBase64 = pendingImageBase64Ref.current;
    if (pendingImageBase64 !== null) {
      setPendingUpload(null);
      pendingImageBase64Ref.current = null;
    }

    // Consume pending document once — use refs (always current) not state (stale closure).
    const pendingDocumentBase64 = pendingDocumentBase64Ref.current;
    const pendingDocumentName = pendingDocumentFileRef.current?.name ?? null;
    if (pendingDocumentBase64 !== null || pendingDocumentFileRef.current !== null) {
      setPendingUpload(null);
      pendingDocumentBase64Ref.current = null;
      pendingDocumentFileRef.current = null;
    }

    const userMessage: AgentMessage = { role: 'user', text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setExpanded(true);
    setIsThinking(true);
    const requestStartedAt = Date.now();
    setLiveAgentText('');
    liveAgentTextRef.current = '';
    setLiveStatus('');
    setLastStreamEventAt(null);
    setLiveEntries([]);
    liveEntriesRef.current = [];
    liveEntryIdRef.current = 0;
    const nextConversation = [...messages.slice(-5), userMessage];

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const cameraPosition       = getCameraPosition?.() ?? undefined;
      const viewerSpatialContext = getViewerSpatialContext?.() ?? undefined;
      const baseRequest = {
        prompt: text,
        scene,
        capabilities: buildViewerAgentCapabilities(scene, capabilities),
        visiblePOIs: pois,
        conversation: nextConversation,
        cameraPosition,
        viewerSpatialContext,
        conversationId: conversationIdRef.current,
        language: lang as 'en' | 'fr',
        ...(pendingDocumentBase64 ? {
          uploadedDocumentBase64: pendingDocumentBase64,
          uploadedDocumentName: pendingDocumentName ?? undefined,
        } : {}),
        ...(pendingImageBase64 ? { screenshotBase64: pendingImageBase64 } : {}),
      };

      const handleStreamEvent = (event: ViewerAgentStreamEvent) => {
        setLastStreamEventAt(Date.now());

        if (event.type === 'status') {
          setLiveStatus(event.content);
          return;
        }

        // ── Legacy SI events (old architecture, kept for fallback) ──────────
        if (event.type === 'spatialIntelligence') {
          const siEvent = event.event;
          if (siEvent.type === 'status') {
            setLiveStatus(siEvent.content);
            appendLiveEntry({ kind: 'progress', text: siEvent.content });
          } else if (siEvent.type === 'text') {
            setLiveAgentText((prev) => {
              const next = `${prev}${siEvent.content}`;
              liveAgentTextRef.current = next;
              return next;
            });
            splitStreamText(siEvent.content).forEach((chunk) => {
              appendLiveEntry({ kind: 'analysis', text: chunk });
            });
          } else if (siEvent.type === 'tool') {
            const label = toolProgressLabel(siEvent.name, siEvent.input);
            setLiveStatus(label);
            appendLiveEntry({ kind: 'progress', text: label });
          } else if (siEvent.type === 'result' && siEvent.files.length > 0) {
            siEvent.files.forEach((file) => {
              appendLiveEntry({ kind: 'file', text: file });
            });
            setLiveAgentText((prev) => {
              const next = [prev, '', `**${va.outputFile}:**`, ...siEvent.files.map((f) => `- ${f}`)].filter(Boolean).join('\n');
              liveAgentTextRef.current = next;
              return next;
            });
          } else if (siEvent.type === 'warning') {
            setLiveStatus(siEvent.content);
            appendLiveEntry({ kind: 'warning', text: siEvent.content });
          } else if (siEvent.type === 'raw') {
            const label = `SI raw ${siEvent.phase}/${siEvent.eventType}: ${truncateMiddle(siEvent.raw, 220)}`;
            setLiveStatus(label);
            appendLiveEntry({ kind: 'progress', text: label });
          } else if (siEvent.type === 'done') {
            setLiveStatus(va.siTurnComplete);
            appendLiveEntry({ kind: 'progress', text: va.siTurnComplete });
          }
          return;
        }

        // ── New SI delegation events ────────────────────────────────────────
        if (event.type === 'delegate') {
          setLiveStatus(va.handingOff);
          appendLiveEntry({ kind: 'progress', text: va.connectingSI });
          return;
        }
        if (event.type === 'si_text') {
          setLiveAgentText((prev) => {
            const next = `${prev}${event.text}`;
            liveAgentTextRef.current = next;
            return next;
          });
          splitStreamText(event.text).forEach((chunk) => {
            appendLiveEntry({ kind: 'analysis', text: chunk });
          });
          return;
        }
        if (event.type === 'si_tool_start') {
          const label = va.runningTool(event.toolName ?? '');
          setLiveStatus(label);
          appendLiveEntry({ kind: 'progress', text: label });
          return;
        }
        if (event.type === 'si_result') {
          event.files.forEach((file) => appendLiveEntry({ kind: 'file', text: file }));
          if (event.files.length > 0) {
            setLiveAgentText((prev) => {
              const next = [prev, '', `**${va.outputFile}:**`, ...event.files.map((f) => `- ${f}`)].filter(Boolean).join('\n');
              liveAgentTextRef.current = next;
              return next;
            });
          }
          return;
        }
        if (event.type === 'si_warning') {
          setLiveStatus(event.message);
          appendLiveEntry({ kind: 'warning', text: event.message });
          return;
        }
        if (event.type === 'si_done') {
          setLiveStatus(va.siAnalysisComplete);
          appendLiveEntry({ kind: 'progress', text: va.siAnalysisComplete });
          return;
        }
        if (event.type === 'si_raw') {
          // debug-only — not shown in customer UI
          return;
        }
        if (event.type === 'si_error') {
          appendLiveEntry({ kind: 'warning', text: va.siError(event.message) });
          return;
        }
        if (event.type === 'si_pending_task') {
          if (pendingTaskTimerRef.current) clearTimeout(pendingTaskTimerRef.current);
          pendingTaskTimerRef.current = setTimeout(() => {
            setPendingTaskPrompt(event.originalQuestion);
          }, 3 * 60 * 1000);
          return;
        }
      };

      let response = await sendViewerAgentMessageStream(baseRequest, handleStreamEvent, abortController.signal);

      // Two-call flow: gap check signalled it needs a screenshot
      if (
        response.pipelineStage === 'gapCheck' &&
        response.requiresUserInput?.type === 'selectPoint' &&
        captureScreenshot
      ) {
        const screenshotBase64 = await captureScreenshot();
        if (screenshotBase64) {
          response = await sendViewerAgentMessageStream({ ...baseRequest, screenshotBase64 }, handleStreamEvent, abortController.signal);
        }
      }

      // UI safety net: opening an upload control should not depend on the model
      // emitting its optional action tool. This mirrors the direct 3D upload flow.
      // Skip when "template" is in the prompt — the server already handled it as a
      // report-generation request and returned a link, not an upload prompt.
      const normalizedPrompt = text.toLowerCase();
      const isTemplatePrompt = /\btemplate\b/.test(normalizedPrompt);
      if (
        (response.decision?.action ?? 'none') === 'none'
        && !baseRequest.uploadedDocumentBase64
        && !isTemplatePrompt
        && /\breport\b/.test(normalizedPrompt)
        && /\b(upload|attach|provide|share|use|based on)\b/.test(normalizedPrompt)
      ) {
        response = {
          ...response,
          decision: {
            ...response.decision,
            intent: 'spatialAnalysis',
            action: 'activateReportTemplateUpload',
            response: response.decision?.response ?? response.message,
          },
        };
      }

      // Pad fast sceneKnowledge responses so they feel like real spatial analysis.
      // Skipped when real SI streaming was involved (liveEntries already populated).
      const MINIMUM_THINKING_MS = 5_000;
      const elapsed = Date.now() - requestStartedAt;
      const siWasInvolved = liveEntriesRef.current.length > 0;
      if (elapsed < MINIMUM_THINKING_MS && !siWasInvolved && !abortController.signal.aborted) {
        const remaining = MINIMUM_THINKING_MS - elapsed;
        const stages = va.paddingStages;
        const step = remaining / stages.length;
        for (const stage of stages) {
          if (abortController.signal.aborted) break;
          setLiveStatus(stage);
          await new Promise<void>((res) => setTimeout(res, step));
        }
        setLiveStatus('');
      }

      // Capture live entries before they clear in finally, attach to the message
      const capturedEntries = liveEntriesRef.current.length > 0
        ? [...liveEntriesRef.current]
        : undefined;
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          text: response.message,
          siEntries: capturedEntries,
          pendingDelegation: response.pendingDelegation,
          encroachmentRows: response.renderAs === 'encroachment_table'
            ? (response.data?.encroachmentRows ?? [])
            : undefined,
          assetUrl: response.decision?.action === 'showAsset' ? response.data?.assetUrl : undefined,
          mtlUrl: response.decision?.action === 'showAsset' ? (response.data as Record<string, unknown> | undefined)?.['mtlUrl'] as string | undefined : undefined,
          textureUrls: response.decision?.action === 'showAsset' ? (response.data as Record<string, unknown> | undefined)?.['textureUrls'] as Record<string, string> | undefined : undefined,
          siFiles: (response.data as Record<string, unknown> | undefined)?.['siFiles'] as string[] | undefined,
          siFileSizes: (response.data as Record<string, unknown> | undefined)?.['siFileSizes'] as number[] | undefined,
          gausViewerHints: (response.data as Record<string, unknown> | undefined)?.['gausViewerHints'] as { lookAt: [number, number, number]; camPos: [number, number, number]; fov?: number; halfSize?: number } | undefined,
          dimensionPolygons: (response.data as Record<string, unknown> | undefined)?.['dimensionPolygons'] as DimensionPolygon[] | undefined,
          warnings: response.warnings,
        },
      ]);

      if (response.decision?.action === 'showAsset' && response.data?.assetUrl) {
        const assetUrl = response.data.assetUrl;
        if (assetUrl.toLowerCase().includes('.gaus')) {
          const hints = (response.data as Record<string, unknown>)?.['gausViewerHints'] as { camPos?: [number, number, number]; lookAt?: [number, number, number]; fov?: number } | undefined;
          let viewerUrl = `/viewer?scene=${encodeURIComponent(scene.sceneId)}&gausOverride=${encodeURIComponent(assetUrl)}`;
          if (hints?.camPos) viewerUrl += `&camPos=${hints.camPos.join(',')}`;
          if (hints?.lookAt) viewerUrl += `&lookAt=${hints.lookAt.join(',')}`;
          if (hints?.fov) viewerUrl += `&fov=${hints.fov}`;
          window.open(viewerUrl, '_blank');
        } else {
          const mtlUrl = (response.data as Record<string, unknown>)?.['mtlUrl'] as string | undefined;
          const textureUrls = (response.data as Record<string, unknown>)?.['textureUrls'] as Record<string, string> | undefined;
          openArtifact(getAgentArtifact(assetUrl, { mtlUrl, textureUrls }));
        }
      }

      if (response.decision?.action === 'generateImageCandidates') {
        const genPrompt = (response.data as Record<string, unknown> | undefined)?.['generationPrompt'] as string | undefined;
        const requestedPlaceWithErase = (response.data as Record<string, unknown> | undefined)?.['placeWithErase'] as boolean | undefined;
        const suggestedDimensions = (response.data as Record<string, unknown> | undefined)?.['suggestedDimensions'] as { width?: number; depth?: number; length?: number } | undefined;
        console.log('[VOA] generateImageCandidates suggestedDimensions:', suggestedDimensions);
        // The click-to-place continuation is currently a trench-specific protocol.
        // Do not route houses or other replacement objects into [TRENCH_PLACEMENT]
        // even if an upstream response accidentally carries placeWithErase.
        const placeWithErase = Boolean(requestedPlaceWithErase && /\b(trench|ditch|excavat|pipe cut|dig)\b/i.test(genPrompt));
        if (genPrompt) {
          setGenerationSession({ sceneId: scene.sceneId, prompt: genPrompt, fluxJob: null, trellisJob: null, selected: null, error: null, placeWithErase: placeWithErase ?? false, suggestedDimensions });
          void createFluxJob(scene.sceneId, genPrompt, 3)
            .then(job => setGenerationSession(prev => prev ? { ...prev, fluxJob: job } : prev))
            .catch(err => setGenerationSession(prev => prev ? { ...prev, error: err instanceof Error ? err.message : 'Image generation failed' } : prev));
          onDecision({ ...response, decision: { ...response.decision!, action: 'none' } });
        } else {
          onDecision(response);
        }
      } else if (response.decision?.action === 'activateObjectUpload') {
        setObjectUploadSession({ sceneId: scene.sceneId, file: null, label: 'Generated object', job: null, error: null, configured: null });
        onDecision({ ...response, decision: { ...response.decision!, action: 'none' } });
      } else if (response.decision?.action === 'activateModelUpload') {
        setModelUploadSession({ file: null, label: 'My 3D model', error: null });
        onDecision({ ...response, decision: { ...response.decision!, action: 'none' } });
      } else if (response.decision?.action === 'activateReportTemplateUpload') {
        // Pre-fill with the document the user already uploaded if available
        setReportTemplateSession({ file: pendingDocumentFileRef.current, title: 'Construction Progress Report', generating: false, error: null });
        onDecision({ ...response, decision: { ...response.decision!, action: 'none' } });
      } else if (response.decision?.action === 'offerEraseOptions') {
        setEraseOfferSession({ userQuery: text });
        onDecision({ ...response, decision: { ...response.decision!, action: 'none' } });
      } else if (response.decision?.action === 'openPage' && response.data?.pageUrl) {
        window.open(response.data.pageUrl, '_blank');
        onDecision({ ...response, decision: { ...response.decision!, action: 'none' } });
      } else {
        onDecision(response);
      }
    } catch (error) {
      const isAbort = (error as Error)?.name === 'AbortError';
      const capturedEntries = liveEntriesRef.current.length > 0
        ? [...liveEntriesRef.current]
        : undefined;
      if (isAbort) {
        setMessages((prev) => [...prev, { role: 'agent', text: va.cancelled, siEntries: capturedEntries }]);
      } else {
        console.warn('[ViewerAgent] Agent request failed:', error);
        const errorMessage =
          liveAgentTextRef.current.trim().length > 0
            ? `${liveAgentTextRef.current}\n\n${va.connStoppedWithText}`
            : va.connStoppedNoText;
        setMessages((prev) => [...prev, { role: 'agent', text: errorMessage, siEntries: capturedEntries }]);
        onDecision({
          success: false,
          source: 'localFallback',
          message: errorMessage,
          renderAs: 'text',
          decision: { intent: 'unknown', action: 'none', response: errorMessage },
        });
      }
    } finally {
      abortControllerRef.current = null;
      setIsThinking(false);
      setLiveAgentText('');
      liveAgentTextRef.current = '';
      setLiveStatus('');
      setLastStreamEventAt(null);
      setLiveEntries([]);
    }
  }, [isThinking, messages, scene, capabilities, pois, getCameraPosition, getViewerSpatialContext, captureScreenshot, onDecision, appendLiveEntry, openArtifact]);

  useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);
  useEffect(() => { welcomeQuestionRef.current = t.viewerAgent.welcomeQuestion; }, [t]);
  useEffect(() => { if (externalSubmitRef) externalSubmitRef.current = handleSubmit; }, [handleSubmit, externalSubmitRef]);

  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const handleTeleportToRow = useCallback((row: EncroachmentRow) => {
    onDecision({
      success: true,
      source: 'claude',
      message: va.flyingTo(row.label),
      renderAs: 'text',
      decision: { intent: 'navigation', action: 'teleportToCoordinates', response: va.flyingTo(row.label) },
      data: {
        teleportTarget: row.viewpointWorld
          ? { world: row.viewpointWorld, lookAtWorld: row.world }
          : { world: row.world },
      },
    });
  }, [onDecision]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking, generationSession]);

  // Typewriter auto-submit: types the welcome prompt into the input, then submits.
  // Depends only on `disabled` so re-renders / handleSubmit reference changes
  // don't cancel the interval mid-type.
  useEffect(() => {
    if (disabled || !ready || autoSubmittedRef.current || import.meta.env.DEV) return;
    autoSubmittedRef.current = true;

    const fullText = welcomeQuestionRef.current;
    let i = 0;
    setExpanded(true);

    let submitTimeout: ReturnType<typeof setTimeout> | null = null;

    const interval = setInterval(() => {
      if (userInteractedRef.current) {
        clearInterval(interval);
        return;
      }
      i++;
      setInput(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(interval);
        submitTimeout = setTimeout(() => {
          if (!userInteractedRef.current) handleSubmitRef.current?.(fullText);
        }, 400);
      }
    }, 75);

    return () => {
      clearInterval(interval);
      if (submitTimeout !== null) clearTimeout(submitTimeout);
      // Reset so the real mount (after StrictMode's phantom mount+cleanup cycle) can proceed
      autoSubmittedRef.current = false;
    };
  }, [disabled, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || disabled || isThinking) return;
    handleSubmit(text);
  };

  // Hide the auto-submitted welcome Q&A (first 2 messages) once the user has asked a real question
  const hasWelcomePair = messages.length >= 2 && messages[0].role === 'user' && messages[1].role === 'agent';
  const displayMessages = hasWelcomePair && messages.length > 2 ? messages.slice(2) : messages;

  const hasEarlierMessages = displayMessages.length > DEFAULT_VISIBLE_MESSAGE_COUNT;
  const visibleMessages = showFullHistory
    ? displayMessages
    : displayMessages.slice(-DEFAULT_VISIBLE_MESSAGE_COUNT);

  if (minimized) {
    return (
      <div ref={chatRootRef} className="fixed left-4 bottom-20 z-[1003]">
        <button
          type="button"
          onClick={() => setMinimized(false)}
          aria-label={va.openChat}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-black/55 text-[#0CB4E4] shadow-lg transition-opacity hover:opacity-80"
          style={{
            backdropFilter: 'blur(10px) saturate(140%)',
            WebkitBackdropFilter: 'blur(10px) saturate(140%)',
          }}
        >
          <Bot className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={chatRootRef} className="fixed left-4 bottom-20 z-[1003] w-[min(400px,calc(100vw-2rem))] text-white">
      {expanded && (messages.length > 0 || isThinking || !!pendingUpload) && (
        <div
          className="mb-2 max-h-[min(68vh,620px)] overflow-y-auto rounded-lg border border-white/10 bg-black/70 p-3 shadow-lg"
          style={{
            backdropFilter: 'blur(10px) saturate(140%)',
            WebkitBackdropFilter: 'blur(10px) saturate(140%)',
          }}
        >
          {messages.length > 0 && (
            <div className="flex justify-end mb-1">
              <button
                type="button"
                title="Start over"
                onClick={() => { setMessages([]); setShowFullHistory(false); }}
                className="text-white/30 hover:text-white/60 transition-colors"
              >
                <RotateCcw size={13} />
              </button>
            </div>
          )}
          {pendingTaskPrompt && !isThinking && (
            <div className="mb-2 rounded-md border border-[#0CB4E4]/30 bg-[#0CB4E4]/10 px-3 py-2 text-sm text-white/80">
              <p className="mb-1.5 leading-snug">{va.pendingResultsReady}</p>
              <p className="mb-2 italic text-white/55 text-xs leading-snug">"{pendingTaskPrompt.length > 120 ? pendingTaskPrompt.slice(0, 120) + '…' : pendingTaskPrompt}"</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleSubmit(va.checkResultsPrompt(pendingTaskPrompt!))}
                  className="rounded px-3 py-1 text-xs font-semibold transition-colors"
                  style={{ background: 'rgba(253,186,89,0.22)', color: '#fff' }}
                >
                  {va.checkNow}
                </button>
                <button
                  type="button"
                  onClick={() => { if (pendingTaskTimerRef.current) { clearTimeout(pendingTaskTimerRef.current); pendingTaskTimerRef.current = null; } setPendingTaskPrompt(null); }}
                  className="rounded px-3 py-1 text-xs font-semibold text-white/40 transition-colors hover:text-white/60"
                >
                  {va.dismiss}
                </button>
              </div>
            </div>
          )}

          {hasEarlierMessages && (
            <button
              type="button"
              onClick={() => setShowFullHistory((prev) => !prev)}
              className="mb-2 w-full border-b border-white/10 pb-2 text-left text-xs font-medium text-[#0CB4E4] transition-colors hover:text-[#ffd08a]"
            >
              {showFullHistory ? va.hideHistory : va.viewHistory}
            </button>
          )}

          {visibleMessages.map((message, index) => (
            <div
              key={`${message.role}-${displayMessages.length - visibleMessages.length + index}`}
              className={`mb-2 rounded-md px-2 py-1.5 text-sm leading-snug ${
                message.role === 'user'
                  ? 'ml-8 bg-white/[0.05] text-white/65 italic'
                  : 'mr-4 bg-white/[0.08] text-white'
              }`}
            >
              {message.role === 'agent' ? (
                <>
                  <AgentMarkdown text={message.text} onPillClick={onPillClick} />
                  {message.encroachmentRows && message.encroachmentRows.length > 0 && (
                    <EncroachmentTable rows={message.encroachmentRows} onTeleport={handleTeleportToRow} />
                  )}
                  {message.assetUrl && (() => {
                    const artifact = getAgentArtifact(message.assetUrl!, { mtlUrl: message.mtlUrl, textureUrls: message.textureUrls });
                    return (
                      <div className="mt-2 flex items-start gap-2 rounded border border-emerald-300/25 bg-emerald-400/10 px-2 py-1.5">
                        <span className="mt-0.5 shrink-0 text-[10px] text-emerald-300/70">↳</span>
                        <div className="min-w-0 flex-1">
                          <code className="block text-xs font-semibold text-emerald-100">{artifact.label}</code>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {!message.assetUrl!.toLowerCase().includes('.gaus') && (
                              <button
                                type="button"
                                onClick={() => openArtifact(artifact)}
                                className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                {artifact.kind === 'pins' ? t.pins.load : va.preview}
                              </button>
                            )}
                            {!message.assetUrl!.toLowerCase().includes('.gaus') && (
                              <a
                                href={message.assetUrl}
                                target="_blank"
                                rel="noreferrer"
                                download={artifact.label}
                                className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                              >
                                <Download className="h-3.5 w-3.5" />
                                {va.download}
                              </a>
                            )}
                            {message.assetUrl!.toLowerCase().includes('.gaus') && (
                              <button
                                type="button"
                                onClick={() => {
                                  let viewerUrl = `/viewer?scene=${encodeURIComponent(scene.sceneId)}&gausOverride=${encodeURIComponent(message.assetUrl!)}`;
                                  const hints = message.gausViewerHints;
                                  if (hints) {
                                    viewerUrl += `&camPos=${hints.camPos.join(',')}&lookAt=${hints.lookAt.join(',')}`;
                                    if (hints.fov) viewerUrl += `&fov=${hints.fov}`;
                                  }
                                  const hs = hints?.halfSize ?? 80;
                                  const size = Math.min(900, Math.max(500, Math.round(400 + hs * 3.5)));
                                  const left = Math.round((window.screen.width  - size) / 2);
                                  const top  = Math.round((window.screen.height - size) / 2);
                                  window.open(viewerUrl, '_blank', `width=${size},height=${size},left=${left},top=${top},resizable=yes,scrollbars=no`);
                                }}
                                className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                {va.openInViewer}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {message.siFiles && message.siFiles.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {message.siFiles.map((url, fileIdx) => {
                        const artifact = getAgentArtifact(url);
                        const fileSizeBytes = message.siFileSizes?.[fileIdx] ?? 0;
                        const isLargeFile = fileSizeBytes > 200 * 1024 * 1024;
                        return (
                          <div key={url} className="flex items-start gap-2 rounded border border-emerald-300/25 bg-emerald-400/10 px-2 py-1.5">
                            <span className="mt-0.5 shrink-0 text-[10px] text-emerald-300/70">↳</span>
                            <div className="min-w-0 flex-1">
                              <code className="block text-xs font-semibold text-emerald-100">{artifact.label}</code>
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {artifact.canPreview && (
                                  <button
                                    type="button"
                                    onClick={() => openArtifact(artifact)}
                                    className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    {artifact.kind === 'pins' ? t.pins.load : va.preview}
                                  </button>
                                )}
                                {url.toLowerCase().includes('.gaus') && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      let viewerUrl = `/viewer?scene=${encodeURIComponent(scene.sceneId)}&gausOverride=${encodeURIComponent(url)}`;
                                      const hints = message.gausViewerHints;
                                      if (hints) {
                                        viewerUrl += `&camPos=${hints.camPos.join(',')}&lookAt=${hints.lookAt.join(',')}`;
                                        if (hints.fov) viewerUrl += `&fov=${hints.fov}`;
                                      }
                                      if (isLargeFile) {
                                        // Large file (>200 MB): open as a full new tab to avoid memory
                                        // contention with the current scene in the same browser context.
                                        window.open(viewerUrl, '_blank');
                                      } else {
                                        const hs = hints?.halfSize ?? 80;
                                        const size = Math.min(900, Math.max(500, Math.round(400 + hs * 3.5)));
                                        const left = Math.round((window.screen.width  - size) / 2);
                                        const top  = Math.round((window.screen.height - size) / 2);
                                        window.open(viewerUrl, '_blank', `width=${size},height=${size},left=${left},top=${top},resizable=yes,scrollbars=no`);
                                      }
                                    }}
                                    className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    {va.openInViewer}
                                  </button>
                                )}
                                {artifact.url && !url.toLowerCase().includes('.gaus') && (
                                  <a
                                    href={artifact.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={artifact.kind === 'image' || artifact.kind === 'obj' ? artifact.label : undefined}
                                    className="inline-flex h-7 items-center gap-1.5 rounded border border-emerald-200/20 bg-emerald-200/10 px-2 text-[11px] font-medium text-emerald-50 transition-colors hover:bg-emerald-200/16"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    {va.download}
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {message.warnings && message.warnings.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1">
                      {message.warnings.map((w, i) => (
                        <p key={i} className="text-[11px] leading-snug text-amber-300/80">{w}</p>
                      ))}
                    </div>
                  )}
                  {message.dimensionPolygons && message.dimensionPolygons.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-white/40">{va.detectedFeatures}</div>
                      {message.dimensionPolygons.map((poly) => (
                        <div key={poly.id} className="flex items-center justify-between gap-2 rounded border border-sky-300/25 bg-sky-400/10 px-2 py-1.5">
                          <span className="text-xs text-white/80">{poly.label}</span>
                          <button
                            type="button"
                            onClick={() => onDecision({
                              success: true,
                              source: 'claude',
                              message: `Showing ${poly.label} in 3D`,
                              renderAs: 'text',
                              decision: { intent: 'measurement', action: 'activateMeasurement', response: '' },
                              data: { measureInitialPoints: poly.worldCorners },
                            })}
                            className="inline-flex h-6 shrink-0 items-center gap-1 rounded border border-sky-200/20 bg-sky-200/10 px-2 text-[11px] font-medium text-sky-100 transition-colors hover:bg-sky-200/16"
                          >
                            <Eye className="h-3 w-3" />
                            {va.viewIn3D}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {message.siEntries && message.siEntries.length > 0 && (
                    <SITrail entries={message.siEntries} onOpenArtifact={openArtifact} />
                  )}
                </>
              ) : (
                message.text
              )}
            </div>
          ))}

          {/* SI thinking block — single expanding panel, live while streaming */}
          {liveEntries.length > 0 && (
            <div className="my-3 rounded-md border border-white/10 bg-white/[0.04] px-3 py-2.5">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#0CB4E4]/80">
                  Spatial Intelligence
                </span>
                {isThinking && (
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#0CB4E4]/50" />
                )}
              </div>
              <SIContent entries={liveEntries} onOpenArtifact={openArtifact} />
            </div>
          )}

          {/* Thinking box: current step + animated phrase */}
          {isThinking && (
            <div className="mt-2 rounded-md border border-white/8 bg-black/25 px-2.5 py-2">
              <ThinkingIndicator currentStatus={liveStatus} lastStreamEventAt={lastStreamEventAt} phrases={va.thinkingPhrases} strings={va} />
            </div>
          )}

          {/* Inline report-template upload widget (SI result → branded PDF) */}
          {reportTemplateSession && (
            <div className="my-3 rounded-xl border border-white/15 bg-[#0E0D0C]/90 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Create report from template</span>
                <button type="button" disabled={reportTemplateSession.generating} onClick={() => setReportTemplateSession(null)} className="text-sm leading-none text-white/40 hover:text-white disabled:opacity-40">✕</button>
              </div>
              <p className="mb-3 text-[11px] text-white/45">Upload a PDF or DOCX template. PDF branding is retained as a cover; DOCX headings, placeholders, tables, and section order guide how the latest Spatial Intelligence findings are organised. Output is always PDF.</p>

              <label className="mb-1.5 block text-xs text-white/55">Report template</label>
              <input
                type="file"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.docx"
                disabled={reportTemplateSession.generating}
                onChange={e => setReportTemplateSession(prev => prev ? { ...prev, file: e.target.files?.[0] ?? null, error: null } : prev)}
                className="mb-3 block w-full text-xs text-white/60 file:mr-3 file:rounded file:border-0 file:bg-white/10 file:px-2 file:py-1 file:text-xs file:text-white"
              />

              <label className="mb-1.5 block text-xs text-white/55">Report title</label>
              <input
                value={reportTemplateSession.title}
                maxLength={160}
                disabled={reportTemplateSession.generating}
                onChange={e => setReportTemplateSession(prev => prev ? { ...prev, title: e.target.value } : prev)}
                onKeyDownCapture={e => e.stopPropagation()}
                onKeyUpCapture={e => e.stopPropagation()}
                className="mb-3 w-full rounded border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none disabled:opacity-50"
              />

              {reportTemplateSession.error && <p className="mb-3 text-xs text-red-300">{reportTemplateSession.error}</p>}
              <button
                type="button"
                disabled={!reportTemplateSession.file || reportTemplateSession.generating}
                onClick={() => void generateReportFromTemplate()}
                className="w-full rounded bg-[#0CB4E4] py-2 text-sm font-semibold text-black disabled:opacity-40"
              >
                {reportTemplateSession.generating ? 'Generating PDF…' : 'Generate and download PDF'}
              </button>
            </div>
          )}

          {/* Inline direct model upload widget (.glb / .gltf → place immediately) */}
          {modelUploadSession && (
            <div className="my-3 rounded-xl border border-white/15 bg-[#0E0D0C]/90 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Upload your 3D model</span>
                <button type="button" onClick={() => setModelUploadSession(null)} className="text-white/40 hover:text-white text-sm leading-none">✕</button>
              </div>
              <p className="mb-3 text-[11px] text-white/45">Accepts .glb and .gltf files. The model will be placed in the scene and you can reposition and scale it with the toolbox.</p>

              <label className="mb-1.5 block text-xs text-white/55">3D model file</label>
              <input
                type="file"
                accept=".glb,.gltf"
                onChange={e => {
                  const f = e.target.files?.[0] ?? null;
                  setModelUploadSession(prev => prev ? {
                    ...prev,
                    file: f,
                    label: f ? f.name.replace(/\.[^.]+$/, '') : prev.label,
                    error: null,
                  } : prev);
                }}
                className="mb-3 block w-full text-xs text-white/60 file:mr-3 file:rounded file:border-0 file:bg-white/10 file:px-2 file:py-1 file:text-xs file:text-white"
              />

              <label className="mb-1.5 block text-xs text-white/55">Asset name</label>
              <input
                value={modelUploadSession.label}
                maxLength={100}
                onChange={e => setModelUploadSession(prev => prev ? { ...prev, label: e.target.value } : prev)}
                onKeyDownCapture={e => e.stopPropagation()}
                onKeyUpCapture={e => e.stopPropagation()}
                className="mb-4 w-full rounded border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none"
              />

              {modelUploadSession.error && (
                <p className="mb-3 text-xs text-red-300">{modelUploadSession.error}</p>
              )}

              <button
                type="button"
                disabled={!modelUploadSession.file}
                onClick={placeModelFromUpload}
                className="w-full rounded bg-[#0CB4E4] py-2 text-sm font-semibold text-black disabled:opacity-40"
              >
                Place in scene →
              </button>
            </div>
          )}

          {/* Inline object-upload widget (user's own image → 3D) */}
          {objectUploadSession && (
            <div className="my-3 rounded-xl border border-white/15 bg-[#0E0D0C]/90 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Create a 3D object from your image</span>
                <button type="button" onClick={cancelObjectUpload} className="text-white/40 hover:text-white text-sm leading-none">✕</button>
              </div>

              {objectUploadSession.configured === false && (
                <div className="mb-3 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200">
                  Trellis is wired but not configured yet. Add the subscription endpoint and token on the backend to enable generation.
                </div>
              )}

              <label className="mb-1.5 block text-xs text-white/55">Object image</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={Boolean(objectUploadSession.job && !GENERATION_TERMINAL.has(objectUploadSession.job.status)) || objectUploadSession.configured === false}
                onChange={e => setObjectUploadSession(prev => prev ? { ...prev, file: e.target.files?.[0] ?? null } : prev)}
                className="mb-3 block w-full text-xs text-white/60 file:mr-3 file:rounded file:border-0 file:bg-white/10 file:px-2 file:py-1 file:text-xs file:text-white"
              />

              <label className="mb-1.5 block text-xs text-white/55">Asset name</label>
              <input
                value={objectUploadSession.label}
                disabled={Boolean(objectUploadSession.job && !GENERATION_TERMINAL.has(objectUploadSession.job.status))}
                maxLength={100}
                onChange={e => setObjectUploadSession(prev => prev ? { ...prev, label: e.target.value } : prev)}
                onKeyDownCapture={e => e.stopPropagation()}
                onKeyUpCapture={e => e.stopPropagation()}
                className="mb-4 w-full rounded border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none disabled:opacity-50"
              />

              {objectUploadSession.job && (
                <div className="mb-3">
                  <div className="mb-1 flex justify-between text-xs text-white/50">
                    <span>{objectUploadSession.job.status}</span>
                    <span>{objectUploadSession.job.progress}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded bg-white/10">
                    <div className="h-full bg-[#0CB4E4] transition-all duration-500" style={{ width: `${objectUploadSession.job.progress}%` }} />
                  </div>
                </div>
              )}

              {(objectUploadSession.error || objectUploadSession.job?.error) && (
                <p className="mb-3 text-xs text-red-300">{objectUploadSession.error ?? objectUploadSession.job?.error?.message}</p>
              )}

              <div className="flex gap-2">
                {objectUploadSession.job && !GENERATION_TERMINAL.has(objectUploadSession.job.status) ? (
                  <button
                    type="button"
                    onClick={() => {
                      void cancelTrellisJob(objectUploadSession.job!.jobId);
                      setObjectUploadSession(prev => prev ? { ...prev, job: { ...prev.job!, status: 'cancelled' } } : prev);
                    }}
                    className="flex-1 rounded bg-white/10 py-2 text-sm"
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!objectUploadSession.file || objectUploadSession.configured !== true}
                    onClick={() => void startObjectGeneration()}
                    className="flex-1 rounded bg-[#0CB4E4] py-2 text-sm font-semibold text-black disabled:opacity-40"
                  >
                    Generate 3D model
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Inline image generation widget */}
          {generationSession && (
            <div className="my-3 rounded-xl border border-white/15 bg-[#0E0D0C]/90 p-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Choose an image</span>
                <button type="button" onClick={cancelGeneration} className="text-white/40 hover:text-white text-sm leading-none">✕</button>
              </div>
              <textarea
                value={generationSession.prompt}
                onChange={e => setGenerationSession(prev => prev ? { ...prev, prompt: e.target.value } : prev)}
                onKeyDownCapture={e => e.stopPropagation()}
                onKeyUpCapture={e => e.stopPropagation()}
                rows={2}
                disabled={
                  Boolean(generationSession.fluxJob && !GENERATION_TERMINAL.has(generationSession.fluxJob.status)) ||
                  Boolean(generationSession.trellisJob)
                }
                className="mb-3 w-full resize-none rounded border border-white/10 bg-white/5 px-2 py-1.5 text-[11px] leading-snug text-white/70 placeholder-white/25 focus:border-white/25 focus:outline-none disabled:opacity-40"
                placeholder="Describe what you're looking for…"
              />

              {generationSession.fluxJob && !GENERATION_TERMINAL.has(generationSession.fluxJob.status) && (
                <p className="mb-3 text-xs text-[#0CB4E4]">Generating options… {generationSession.fluxJob.progress}%</p>
              )}

              <div className="grid grid-cols-3 gap-2">
                {/* Skeleton placeholders while waiting for candidates */}
                {(!generationSession.fluxJob || (!GENERATION_TERMINAL.has(generationSession.fluxJob.status) && (generationSession.fluxJob.candidates ?? []).length === 0)) &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="aspect-square w-full animate-pulse rounded-lg bg-white/10" />
                  ))
                }
                {/* Terminal with no candidates — service returned nothing */}
                {generationSession.fluxJob &&
                 GENERATION_TERMINAL.has(generationSession.fluxJob.status) &&
                 (generationSession.fluxJob.candidates ?? []).length === 0 && (
                  <p className="col-span-3 py-4 text-center text-[11px] text-white/40">
                    {generationSession.fluxJob.status === 'failed'
                      ? (generationSession.fluxJob.error?.message ?? 'Generation failed — try again or rephrase.')
                      : 'No images were returned — try again or rephrase the description.'}
                  </p>
                )}
                {/* Candidates */}
                {(generationSession.fluxJob?.candidates ?? []).map(candidate => (
                  <button
                    key={candidate.candidateId}
                    type="button"
                    disabled={Boolean(generationSession.trellisJob)}
                    onClick={() => setGenerationSession(prev => prev ? { ...prev, selected: candidate } : prev)}
                    className={`overflow-hidden rounded-lg border-2 text-left transition-colors ${
                      generationSession.selected?.candidateId === candidate.candidateId
                        ? 'border-[#0CB4E4]'
                        : 'border-transparent hover:border-white/30'
                    }`}
                  >
                    <img src={candidate.url} alt="Generated candidate" className="aspect-square w-full object-cover" />
                    <span className="block px-2 py-1 text-[10px] text-white/55">
                      {generationSession.selected?.candidateId === candidate.candidateId ? 'Selected ✓' : 'Select'}
                    </span>
                  </button>
                ))}
              </div>

              {generationSession.trellisJob && (
                <div className="mt-3">
                  <div className="mb-1 flex justify-between text-[11px] text-white/50">
                    <span>Creating 3D model: {generationSession.trellisJob.status}</span>
                    <span>{generationSession.trellisJob.progress}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded bg-white/10">
                    <div className="h-full bg-[#0CB4E4] transition-all duration-500" style={{ width: `${generationSession.trellisJob.progress}%` }} />
                  </div>
                </div>
              )}

              {generationSession.error && (
                <p className="mt-2 text-xs text-red-300">{generationSession.error}</p>
              )}

              {!generationSession.trellisJob && (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    disabled={Boolean(generationSession.fluxJob && !GENERATION_TERMINAL.has(generationSession.fluxJob.status))}
                    onClick={regenerateFlux}
                    className="rounded bg-white/10 px-3 py-1.5 text-xs disabled:opacity-40"
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    disabled={!generationSession.selected || Boolean(generationSession.trellisJob)}
                    onClick={() => void startTrellisFromSelection()}
                    className="ml-auto rounded bg-[#0CB4E4] px-4 py-1.5 text-xs font-semibold text-black disabled:opacity-40"
                  >
                    Use this one →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Erase offer card — shown when agent detects erase intent */}
          {eraseOfferSession && !isThinking && (
            <div className="my-3 rounded-xl border border-red-500/20 bg-red-500/8 p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">How would you like to erase?</span>
                <button
                  type="button"
                  onClick={() => setEraseOfferSession(null)}
                  className="text-white/40 hover:text-white text-sm leading-none"
                >
                  ✕
                </button>
              </div>
              <p className="mb-3 text-[11px] text-white/45 leading-snug">
                {eraseOfferSession.userQuery.length > 80 ? eraseOfferSession.userQuery.slice(0, 80) + '…' : eraseOfferSession.userQuery}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEraseOfferSession(null);
                    onDecision({
                      success: true,
                      source: 'claude',
                      message: "Opening the eraser — brush over the area you want to remove.",
                      renderAs: 'text',
                      decision: { intent: 'spatialAnalysis', action: 'activateEraseMode', response: '' },
                    });
                  }}
                  className="flex-1 rounded border border-white/15 bg-white/8 py-2 text-xs font-medium text-white/80 hover:bg-white/15 transition-colors"
                >
                  Manual brush
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const query = eraseOfferSession.userQuery;
                    setEraseOfferSession(null);
                    void handleSubmit(`Find the coordinates — open the selection tool so I can point to what I want to erase: "${query.slice(0, 60)}"`);
                  }}
                  className="flex-1 rounded bg-red-500/70 py-2 text-xs font-semibold text-white hover:bg-red-500/90 transition-colors"
                >
                  Find coordinates with AI
                </button>
              </div>
            </div>
          )}

          {/* File upload detection card */}
          {pendingUpload && (
            <div className="my-3 rounded-xl border border-white/15 bg-[#0E0D0C]/90 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-white/40" />
                  <span className="text-xs text-white/55 truncate">{pendingUpload.file.name}</span>
                </div>
                <button type="button" onClick={() => setPendingUpload(null)} className="ml-2 shrink-0 text-white/40 hover:text-white text-sm leading-none">✕</button>
              </div>

              {pendingUpload.type === 'image' && (
                <p className="text-sm text-white/70 leading-snug">
                  What would you like me to do with this image?
                </p>
              )}

              {pendingUpload.type === 'model' && (
                <>
                  <p className="mb-3 text-sm text-white/85">Do you want me to place this 3D model in the scene?</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={placeUploadedModel}
                      className="flex-1 rounded bg-[#0CB4E4] py-2 text-xs font-semibold text-black hover:opacity-90 transition-opacity">
                      Yes, place it
                    </button>
                    <button type="button" onClick={() => setPendingUpload(null)}
                      className="rounded border border-white/15 bg-white/8 px-4 py-2 text-xs font-medium text-white/80 hover:bg-white/15 transition-colors">
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {pendingUpload.type === 'document' && (
                <p className="text-sm text-white/85 leading-snug">
                  What would you like me to do with this document?
                </p>
              )}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*,.glb,.gltf,.pdf,.doc,.docx"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFileSelected(file);
          e.target.value = '';
        }}
      />

      <form
        onSubmit={submit}
        onKeyDownCapture={stopViewerShortcuts}
        onKeyUpCapture={stopViewerShortcuts}
        className="flex items-center gap-2 rounded-lg border border-white/12 bg-black/55 p-2 shadow-lg"
        style={{
          backdropFilter: 'blur(10px) saturate(140%)',
          WebkitBackdropFilter: 'blur(10px) saturate(140%)',
        }}
      >
        <Bot className="h-5 w-5 shrink-0 text-[#0CB4E4]" />
        <input
          value={input}
          onChange={(event) => {
            userInteractedRef.current = true;
            setInput(event.target.value);
          }}
          onFocus={() => setExpanded(true)}
          onKeyDownCapture={stopViewerShortcuts}
          onKeyUpCapture={stopViewerShortcuts}
          disabled={disabled || isThinking}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/40 disabled:cursor-not-allowed"
        />
        {isThinking ? (
          <button
            type="button"
            onClick={handleStop}
            aria-label={va.cancelAriaLabel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white transition-colors hover:opacity-80"
            style={{ background: 'rgba(239,68,68,0.30)' }}
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={disabled || !input.trim()}
            aria-label={va.sendAriaLabel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            style={{ background: 'rgba(253,186,89,0.22)' }}
          >
            <Send className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => uploadInputRef.current?.click()}
          aria-label="Upload file"
          title="Upload image, 3D model, or document"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
            pendingUpload ? 'text-[#0CB4E4]' : 'text-white/40 hover:text-white/70'
          }`}
        >
          <Paperclip className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          aria-label={va.minimizeChat}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/40 transition-colors hover:text-white/70"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
      </form>

      {selectedArtifact && (
        <AgentArtifactViewer
          artifact={selectedArtifact}
          onClose={() => setSelectedArtifact(null)}
        />
      )}
    </div>
  );
};

export default ViewerOrchestrationAgent;
